import { History } from 'history';
import * as Cookie from 'js-cookie';
import { endsWith, forEach, isEmpty, isNull, isUndefined } from 'lodash';
import * as React from 'react';
import { Query } from 'redux-little-router';

import { CanvasPageType } from '../components/ImageCanvasIFrame/ImageCanvasIFrame';
import { VideoCanvasPageType } from '../components/VideoCanvasIFrame/VideoCanvasIFrame';
import { BannersScreenUrlSearchQueryParams } from '../typings/banners';
import { CampaignsScreenUrlSearchQueryParams } from '../typings/campaigns';
import {
    APP_PREFIX,
    APPS_EDIT_PREFIX,
    banners as BANNERS_LIST_PREFIX,
    BANNERS_EDIT_PREFIX,
    campaigns as CAMPAIGNS_LIST_PREFIX,
    CAMPAIGNS_EDIT_PREFIX,
    CUSTOMERS_PREFIX,
    DEALS_PREFIX,
    FormRequestParams,
    FREELANCERS_PREFIX,
    GRID_PREFIX,
    groups as GROUPS_LIST_PREFIX,
    GROUPS_EDIT_PREFIX,
    INTERNAL_BANNERS_EDIT_PREFIX,
    Lang,
    LIBRARY_PREFIX,
    LinkTarget,
    LOG_PREFIX,
    MINUS_PHRASE_PREFIX,
    RECOMMENDATIONS_PREFIX,
    Tld,
    TOOLS_PREFIX
} from '../typings/common';
import { CampaignType } from '../typings/domain';
import { FreelancerCustomersTabs } from '../typings/freelancers';
import { SortDirection, SortParams } from '../typings/grid';
import { GroupsScreenUrlSearchQueryParams } from '../typings/groups';

import { castStringToEnum } from './CastHelpers';
import { isMacDevice } from './Dom/Dom';
import { mapFreelancerCustomersTabToQueryParam } from './FreelancerCustomersHelper/FreelancerCustomersHelper';
import { LangUtils } from './Lang/Lang';
import { pcodeVer } from './Pcode/PcodeVersion';

export const MAX_LENGTH_URL = 2000;

type Search = History.Search;

export interface UrlSearchParams {
    [index: string]: string | null | undefined | Array<number | string>;
}

export interface UrlObjectParams {
    host?: string;
    protocol?: string;
    port?: string;
    path?: string;
    query?: object;
    hash?: string;
}

export interface LinkExample {
    protocol: string;
    domain: string;
    slash: string;
    path: string;
    querySign: string;
    queryParameters: string;
}

export class UrlHelpers {
    private static oldDirectPath = '/registered/main.pl';
    private static newDirectPath = '/dna';
    private static webApiPath = '/web-api';

    static getBannerAimingUrl(
        login: string,
        bannerId: string,
        cookieTtl: number
    ): string {
        const url = new URL(document.location.href);

        url.pathname = `${this.webApiPath}/banner_aiming/set_cookie`;
        url.search = UrlHelpers.createSearchStringByParams({
            ulogin: login,
            bid: bannerId,
            cookie_ttl: String(Math.floor(cookieTtl)),
            csrf_token: this.getToken(),
            retpath: this.getDirectLink('ok')
        });

        return url.toString();
    }

    static getBannerAimingRollbackUrl(login: string): string {
        const url = new URL(document.location.href);

        url.pathname = `${this.webApiPath}/banner_aiming/reset_cookie`;
        url.search = UrlHelpers.createSearchStringByParams({
            ulogin: login,
            csrf_token: this.getToken(),
            retpath: this.getDirectLink('ok')
        });

        return url.toString();
    }

    static getAdfoxBannerAimingUrl(
        adocSubcookie: string,
        cookieTtl: number
    ): string {
        const adfoxApi = 'https://ads.adfox.ru';

        const url = new URL(adfoxApi);
        url.pathname = 'set_yp';
        url.search = UrlHelpers.createSearchStringByParams({
            yp: adocSubcookie,
            ttl: String(Math.floor(cookieTtl)),
            retpath: this.getDirectLink('ok')
        });

        return url.toString();
    }

    static getLinkExample(): LinkExample {
        return {
            protocol: 'https://',
            domain: 'market.yandex.ru',
            slash: '/',
            path: 'catalog/spaceship/',
            querySign: '?',
            queryParameters: 'track=pieces&local-offers-first=0'
        };
    }

    static getLinkTarget(event: React.MouseEvent<HTMLElement>): LinkTarget {
        return event.ctrlKey || event.metaKey
            ? LinkTarget.blank
            : LinkTarget.self;
    }

    static getPaymentMethodsListUrl(currentLang: Lang): string {
        const tld = {
            [Lang.ru]: 'ru',
            [Lang.uk]: 'ru',
            [Lang.en]: 'com',
            [Lang.tr]: 'com.tr'
        }[currentLang];

        return `https://yandex.${tld}/support/direct/payments/payment-methods.html`;
    }

    static getPassportUrl(retpathInit?: string): string {
        const retpath = retpathInit
            ? `${document.location.origin}${retpathInit}`
            : UrlHelpers.getRetPath();
        return `https://passport.yandex.${UrlHelpers.getTld()}/auth?retpath=${encodeURIComponent(
            retpath
        )}&nosync=1`;
    }

    static getPassportChangePhoneUrl(tld: Tld): string {
        return `https://passport.yandex.${tld}/profile/phones`;
    }

    static getDialogsUrl(path: string, tld: Tld): string {
        return `https://dialogs.yandex.${tld}/${path}`;
    }

    static getLangSwitchUrl(retPath: string, tuneSecretKey: string): string {
        const newLang =
            Lang.ru === LangUtils.getCurrentLang() ? Lang.en : Lang.ru;

        return `https://www.yandex.${UrlHelpers.getTld()}/portal/set/lang/?retpath=${encodeURIComponent(
            retPath
        )}&intl=${newLang}&sk=${tuneSecretKey}`;
    }

    static getShowCampaignUrl(
        id: string,
        login: string,
        phraseId?: string
    ): string {
        return UrlHelpers.getDirectLink(
            'showCamp',
            {
                cid: id,
                ulogin: login
            },
            phraseId ? phraseId : undefined
        );
    }

    static getEditCampUrlParams(cid: string, login: string): UrlSearchParams {
        return {
            cid: cid,
            ulogin: login,
            retpath: UrlHelpers.getRetPath()
        };
    }

    static getEditCampUrl(id: string, login: string): string {
        return UrlHelpers.getDirectLink('editCamp', {
            ...UrlHelpers.getEditCampUrlParams(id, login),
            retpath: UrlHelpers.getRetPath()
        });
    }

    static getCreateCampUrl(type: CampaignType, login?: string): string {
        return UrlHelpers.getDirectLink('editCamp', {
            mediaType: type.toLowerCase(),
            new_camp: '1',
            ulogin: login
        });
    }

    static getTurboConstructorUrl(options: {
        host: string;
        clientId?: string;
        selectedId?: number;
        templateName?: string;
        enableTemplates?: string[];
        disableTemplates?: string[];
    }): string {
        const path = '/landings';

        const params = {
            clientId: options.clientId || '',
            creativeType: 'common',
            isCompact: true,
            filters: { sizes: [] },
            areFiltersDisabled: false,
            selected_id: options.selectedId,
            mode: 'select',
            templateName: options.templateName,
            enableTemplates: options.enableTemplates,
            disableTemplates: options.disableTemplates
        };

        return UrlHelpers.FormatUrl({
            host: options.host,
            path: path,
            query: {
                shared_data: encodeURIComponent(JSON.stringify(params))
            }
        });
    }

    static getAddCampUrlParams(
        campaignType: CampaignType,
        login: string
    ): UrlSearchParams {
        const campaignTypeToMediaType: { [key in CampaignType]: string } = {
            [CampaignType.TEXT]: 'text',
            [CampaignType.DYNAMIC]: 'dynamic',
            [CampaignType.MOBILE_CONTENT]: 'mobile_content',
            [CampaignType.PERFORMANCE]: 'performance',
            [CampaignType.MCBANNER]: 'mcbanner',
            [CampaignType.CPM_BANNER]: 'cpm_banner',
            [CampaignType.CPM_DEALS]: 'cpm_deals',
            [CampaignType.CONTENT_PROMOTION]: 'content_promotion',
            [CampaignType.CPM_YNDX_FRONTPAGE]: 'cpm_yndx_frontpage',
            [CampaignType.INTERNAL_DISTRIB]: 'internal_distrib',
            [CampaignType.INTERNAL_FREE]: 'internal_free'
        };

        return {
            new_camp: '1',
            mediaType: campaignTypeToMediaType[campaignType],
            ulogin: login
        };
    }

    static getEditGroupsUrlParams(
        groupIds: string[],
        campaignId: string,
        login: string,
        bannerIds?: string[]
    ): UrlSearchParams {
        return {
            cid: campaignId,
            adgroup_ids: groupIds.join(','),
            bids: bannerIds !== undefined ? bannerIds.join(',') : null,
            ulogin: login,
            retpath: UrlHelpers.getRetPath()
        };
    }

    static getAddGroupsUrlParams(
        campaignId: string,
        login: string
    ): UrlSearchParams {
        return {
            cid: campaignId,
            ulogin: login,
            retpath: UrlHelpers.getRetPath(),
            new_group: '1'
        };
    }

    static getRetPath(): string {
        return decodeURIComponent(document.location.href);
    }

    static getRelativeRetPath(): string {
        return decodeURIComponent(document.location.pathname);
    }

    static generatePath(pathname: string, query: string): string {
        const path = `${document.location.origin}${pathname}${query}`;

        return decodeURIComponent(path);
    }

    static getAddGroupCmdByCampaignType(type: CampaignType): string {
        const dict: { [key in CampaignType]: string } = {
            [CampaignType.TEXT]: 'showCampMultiEdit',
            [CampaignType.MOBILE_CONTENT]: 'editAdGroupsMobileContent',
            [CampaignType.PERFORMANCE]: 'addAdGroupsPerformance',
            [CampaignType.DYNAMIC]: 'editDynamicAdGroups',
            [CampaignType.MCBANNER]: 'editMediaAdGroups',
            [CampaignType.CPM_BANNER]: 'editAdGroupsCpmBanner',
            [CampaignType.CPM_DEALS]: 'editAdGroupsCpmBanner',
            [CampaignType.CONTENT_PROMOTION]:
                'addAdGroupsContentPromotionVideo',
            [CampaignType.CPM_YNDX_FRONTPAGE]: 'editAdGroupsCpmBanner',
            [CampaignType.INTERNAL_DISTRIB]: 'editAdGroupsCpmBanner',
            [CampaignType.INTERNAL_FREE]: 'editAdGroupsCpmBanner'
        };

        return dict[type];
    }

    static getEditGroupCmdByCampaignType(type: CampaignType): string {
        const dict: { [key in CampaignType]: string } = {
            [CampaignType.TEXT]: 'showCampMultiEdit',
            [CampaignType.MOBILE_CONTENT]: 'editAdGroupsMobileContent',
            [CampaignType.PERFORMANCE]: 'editAdGroupsPerformance',
            [CampaignType.DYNAMIC]: 'editDynamicAdGroups',
            [CampaignType.MCBANNER]: 'editMediaAdGroups',
            [CampaignType.CPM_BANNER]: 'editAdGroupsCpmBanner',
            [CampaignType.CPM_DEALS]: 'editAdGroupsCpmBanner',
            [CampaignType.CONTENT_PROMOTION]:
                'editAdGroupsContentPromotionVideo',
            [CampaignType.CPM_YNDX_FRONTPAGE]: 'editAdGroupsCpmBanner',
            [CampaignType.INTERNAL_DISTRIB]: 'editAdGroupsCpmBanner',
            [CampaignType.INTERNAL_FREE]: 'editAdGroupsCpmBanner'
        };

        return dict[type];
    }

    static getEditGroupsUrl(
        groupsIds: string[],
        campaignId: string,
        campaignType: CampaignType,
        login: string
    ): string {
        return UrlHelpers.getDirectLink(
            UrlHelpers.getEditGroupCmdByCampaignType(campaignType),
            UrlHelpers.getEditGroupsUrlParams(groupsIds, campaignId, login)
        );
    }

    static getEditFeedsUrl(login: string): string {
        return UrlHelpers.getDirectLink('showFeeds', {
            ulogin: login,
            popupMode: '1'
        });
    }

    static getPayUrl(id: string, login: string): string {
        return UrlHelpers.getDirectLink('pay', {
            cid: id,
            ulogin: login
        });
    }

    static getWalletUrl(login: string, agencyID: string | null): string {
        return UrlHelpers.getDirectLink('clientWallet', {
            ulogin: login,
            AgencyID: agencyID
        });
    }

    static getReportsUrl(login: string): string {
        return UrlHelpers.getDirectLink('showStat', {
            ulogin: login
        });
    }

    static getCampStatsUrl(
        login: string,
        cid: string,
        dateFrom: string,
        dateTo: string
    ): string {
        return UrlHelpers.getDirectLink('showStat', {
            ulogin: login,
            cid,
            detail: 'Yes',
            types: 'days',
            single_camp: '1',
            stat_type: 'moc',
            show_stat: '1',
            date_from: dateFrom,
            date_to: dateTo
        });
    }

    static getCampaignsMolRequestParams(
        campaignIds: Array<string>,
        login: string,
        statPeriod: { dateFrom: string; dateTo: string },
        target: LinkTarget = LinkTarget.self
    ): FormRequestParams {
        return {
            action: UrlHelpers.oldDirectPath,
            target: target,
            data: {
                cmd: 'showStat',
                stat_type: 'mol',
                show_stat: '1',
                ulogin: login,
                date_from: statPeriod.dateFrom,
                date_to: statPeriod.dateTo,
                json_filters: JSON.stringify({ campaign: { eq: campaignIds } })
            }
        };
    }

    static getCampaignModerationOfferParams(
        campaignId: string,
        login: string,
        target: LinkTarget = LinkTarget.self
    ): FormRequestParams {
        return {
            action: UrlHelpers.oldDirectPath,
            target: target,
            data: {
                cid: campaignId,
                cmd: 'orderCamp',
                ulogin: login,
                csrf_token: this.getToken()
            }
        };
    }

    static getCampaignPaymentInstructionsPage(
        campaignIds: string[],
        ulogin: string,
        target: LinkTarget = LinkTarget.self
    ): FormRequestParams {
        return {
            action: UrlHelpers.oldDirectPath,
            target,
            data: {
                cmd: 'directPaymentInstructions',
                cid: campaignIds,
                ulogin,
                csrf_token: this.getToken()
            }
        };
    }

    static getGroupsMolRequestParams(
        groupsIds: Array<number>,
        login: string,
        statPeriod: { dateFrom: string; dateTo: string },
        target: LinkTarget = LinkTarget.self
    ): FormRequestParams {
        return {
            action: UrlHelpers.oldDirectPath,
            target: target,
            data: {
                cmd: 'showStat',
                stat_type: 'mol',
                show_stat: '1',
                ulogin: login,
                date_from: statPeriod.dateFrom,
                date_to: statPeriod.dateTo,
                json_filters: JSON.stringify({
                    adgroup_id: { eq: groupsIds.join('\n') }
                })
            }
        };
    }

    static getCampaignGroupsEditRequestParams(
        campaignId: string,
        groupsIds: Array<string>,
        login: string,
        target: LinkTarget = LinkTarget.self
    ): FormRequestParams {
        return {
            action: this.oldDirectPath,
            target: target,
            method: 'GET',
            data: {
                cmd: 'showCampMultiEdit',
                banner_status: 'all',
                ulogin: login,
                cid: campaignId,
                adgroup_ids: groupsIds.join(',')
            }
        };
    }

    /**
     * Возвращает SearchString на основе params (создает с нуля)
     */
    static createSearchStringByParams(params: UrlSearchParams): string {
        const searchParams = new URLSearchParams();

        forEach(params, (value, name) => {
            if (isUndefined(value) || isNull(value)) {
                return;
            }

            if (value instanceof Array) {
                searchParams.set(name, value.join(','));
            } else {
                searchParams.set(name, value);
            }
        });

        return searchParams.toString();
    }

    static getRouteQueryByParams(params: UrlSearchParams): Query {
        return Object.keys(params).reduce(
            (acc, key) => {
                const value = params[key];

                if (!isUndefined(value) && !isNull(value)) {
                    if (value instanceof Array) {
                        acc[key] = value.join(',');
                    } else {
                        acc[key] = value;
                    }
                }

                return acc;
            },
            {} as Query
        );
    }

    /**
     * Возвращает URL дополненный параметрами из params (значение null затирает ключ)
     */
    static getSearchStringByParams(params: UrlSearchParams): string {
        const url = new URL(document.location.href);

        forEach(params, (value, name) => {
            if (isUndefined(value)) {
                return;
            }
            if (isNull(value)) {
                if (url.searchParams.get(name)) {
                    url.searchParams.delete(name);
                }
            } else {
                if (value instanceof Array) {
                    url.searchParams.set(name, value.join(','));
                } else {
                    url.searchParams.set(name, value);
                }
            }
        });

        return url.search;
    }

    /**
     * Инвертирует направление сортировки
     */
    static invertSortDirection(direction: SortDirection): SortDirection {
        return direction === SortDirection.asc
            ? SortDirection.desc
            : SortDirection.asc;
    }

    /**
     * Открывает ссылку в текущем или новом окне
     */
    static openUrl(url: string, target?: string): void {
        if (target === LinkTarget.blank) {
            window.open(url, '_blank');
        } else {
            window.location.href = url;
        }
    }

    static getDirectLink(
        cmd: string,
        queryParams: UrlSearchParams = {},
        hash?: string,
        directPath: string = this.oldDirectPath
    ): string {
        const url = new URL(document.location.href);
        const searchParams = Object.assign({}, queryParams, {
            cmd: cmd,
            csrf_token: this.getToken()
        });

        url.pathname = directPath;
        url.search = UrlHelpers.createSearchStringByParams(searchParams);
        if (hash) {
            url.hash = hash;
        }

        return url.toString();
    }

    static FormatUrl(urlObject: UrlObjectParams): string {
        urlObject = urlObject || {};

        const host = urlObject.host || '';
        let protocol = urlObject.protocol || '',
            port = urlObject.port || '',
            path = urlObject.path || '/',
            query = urlObject.query || '',
            hash = urlObject.hash || '';

        if (protocol && protocol.search('://') !== protocol.length - 3) {
            protocol = protocol + '://';
        }
        if (port && port.charAt(0) !== ':') {
            port = ':' + port;
        }
        if (path && path.charAt(0) !== '/') {
            path = '/' + path;
        }
        if (hash && hash.charAt(0) !== '#') {
            hash = '#' + hash;
        }

        if (query) {
            if (typeof query === 'object') {
                query = Object.keys(query)
                    .map(function(key: string): string | null {
                        return query[key] !== undefined
                            ? key + '=' + query[key]
                            : null;
                    })
                    .filter(function(item: string | null): boolean {
                        return item != null;
                    })
                    .join('&');
            }
            if (query.charAt(0) !== '?') {
                query = '?' + query;
            }
        }

        return protocol + host + port + path + query + hash;
    }

    static filterSearchParams(search: Search, whitelist: string[]): string {
        const currentParams = new URLSearchParams(String(search));

        const newParams = new URLSearchParams();

        whitelist.forEach(el => {
            const val = currentParams.get(el);
            if (val) {
                newParams.set(el, val);
            }
        });

        return newParams.toString();
    }

    static createNewDirectLink(
        pathname: string,
        query: UrlSearchParams
    ): string {
        const querySearchParams = !isEmpty(query)
            ? `?${UrlHelpers.createSearchStringByParams(query)}`
            : '';

        return pathname.concat(querySearchParams);
    }

    static createNewDirectUrlPathByParams(
        ...args: (string | undefined)[]
    ): string {
        return [UrlHelpers.newDirectPath, ...args].filter(Boolean).join('/');
    }

    static normalizeSortParams(
        sortKey?: string,
        sortDirection?: string,
        sortGoalId?: string,
        defaultSort?: SortParams
    ): SortParams | undefined {
        if (sortKey) {
            return {
                key: sortKey,
                direction: sortDirection
                    ? castStringToEnum(sortDirection, SortDirection)
                    : SortDirection.desc,
                goalId: sortGoalId
            };
        }

        return defaultSort;
    }

    static getRecommendationsBaseUrl(): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(RECOMMENDATIONS_PREFIX),
            {}
        );
    }

    static getRecommendationsUrl(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(RECOMMENDATIONS_PREFIX),
            {
                ulogin: login
            }
        );
    }

    static getMinusPhrasesLibraryUrl(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                LIBRARY_PREFIX,
                MINUS_PHRASE_PREFIX
            ),
            {
                ulogin: login
            }
        );
    }

    static getBannersEditUrl(params: UrlSearchParams): string {
        const {
            [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignIds,
            [BannersScreenUrlSearchQueryParams.bannersIds]: bannersIds,
            ...otherParams
        } = params;

        const longUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(BANNERS_EDIT_PREFIX),
            params
        );

        const shortUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(BANNERS_EDIT_PREFIX),
            otherParams
        );

        return longUrl.length > MAX_LENGTH_URL ? shortUrl : longUrl;
    }

    static getInternalBannersEditUrl(params: UrlSearchParams = {}): string {
        const {
            [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignIds,
            [BannersScreenUrlSearchQueryParams.bannersIds]: bannersIds,
            ...otherParams
        } = params;

        const longUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                INTERNAL_BANNERS_EDIT_PREFIX
            ),
            params
        );

        const shortUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                INTERNAL_BANNERS_EDIT_PREFIX
            ),
            otherParams
        );

        return longUrl.length > MAX_LENGTH_URL ? shortUrl : longUrl;
    }

    static getGroupsEditUrl(params: UrlSearchParams): string {
        const {
            [GroupsScreenUrlSearchQueryParams.campaignsIds]: campaignIds,
            [GroupsScreenUrlSearchQueryParams.groupsIds]: groupsIds,
            ...otherParams
        } = params;

        const longUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(GROUPS_EDIT_PREFIX),
            params
        );

        const shortUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(GROUPS_EDIT_PREFIX),
            otherParams
        );

        return longUrl.length > MAX_LENGTH_URL ? shortUrl : longUrl;
    }

    static getCampaignsEditUrl(params: UrlSearchParams): string {
        const {
            [CampaignsScreenUrlSearchQueryParams.campaignsIds]: campaignIds,
            ...otherParams
        } = params;

        const longUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(CAMPAIGNS_EDIT_PREFIX),
            params
        );

        const shortUrl = UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(CAMPAIGNS_EDIT_PREFIX),
            otherParams
        );

        return longUrl.length > MAX_LENGTH_URL ? shortUrl : longUrl;
    }

    static getMobileAppsListLink(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(APP_PREFIX, 'list'),
            {
                ulogin: login
            }
        );
    }

    static getMobileAppsCreateLink(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(APP_PREFIX, 'create'),
            {
                ulogin: login
            }
        );
    }

    static getOldCampaignListUrl(login: string): string {
        return UrlHelpers.getDirectLink('showCamps', {
            ulogin: login
        });
    }

    static getCampaignListUrl(params: UrlSearchParams = {}): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                GRID_PREFIX,
                CAMPAIGNS_LIST_PREFIX
            ),
            params
        );
    }

    static getBannersListUrl(params: UrlSearchParams = {}): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                GRID_PREFIX,
                BANNERS_LIST_PREFIX
            ),
            params
        );
    }

    static getGroupsListUrl(params: UrlSearchParams = {}): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                GRID_PREFIX,
                GROUPS_LIST_PREFIX
            ),
            params
        );
    }

    static getFreelancerCardPublicUrl(freelancerLogin: string): string {
        return `https://direct.yandex.ru${UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                FREELANCERS_PREFIX,
                freelancerLogin
            ),
            {}
        )}`;
    }

    static getFreelancerCardUrl(
        login: string,
        freelancerLogin: string
    ): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                FREELANCERS_PREFIX,
                freelancerLogin
            ),
            {
                ulogin: login
            }
        );
    }

    static getLogsUrl(): string {
        return UrlHelpers.createNewDirectUrlPathByParams(LOG_PREFIX);
    }

    static getFreelancerCardEditUrl(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                FREELANCERS_PREFIX,
                login,
                'edit'
            ),
            {
                ulogin: login
            }
        );
    }

    static getFreelancersListUrl(sortParams?: SortParams): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(FREELANCERS_PREFIX),
            {
                ...sortParams
            }
        );
    }

    static getFreelancerCustomers(
        tab: FreelancerCustomersTabs,
        login: string
    ): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(CUSTOMERS_PREFIX),
            {
                tab: mapFreelancerCustomersTabToQueryParam(tab),
                ulogin: login
            }
        );
    }

    static getMobileAppViewLink(id: string, login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(
                APP_PREFIX,
                APPS_EDIT_PREFIX,
                id
            ),
            {
                ulogin: login
            }
        );
    }

    static getDealsViewLink(login: string): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(DEALS_PREFIX),
            {
                ulogin: login
            }
        );
    }

    static extractDomain(url: string): string {
        return url
            .trim()
            .replace(/http(s)?:\/\//, '')
            .replace(/[\/\?].*/, '')
            .replace(/\#.*/, '')
            .replace(/\/$/, '');
    }

    static deleteProtocol(url: string): string {
        return url.trim().replace(/http(s)?:\/\//i, '');
    }

    static extractProtocol(url: string): string | null {
        const matches = url.toLowerCase().match(/http(s)?:\/\//g);
        return matches ? matches[0] : matches;
    }

    static getPublishersListUrl(): string {
        return `https://yandex.ru/adv/partners/private-deals-publishers`;
    }

    static getAbsoluteImageUrl(url: string): string {
        const isDirect = process.env.REACT_APP_TARGET === 'direct';

        if (isDirect) {
            return `${window.location.origin}/${url}`;
        } else {
            return `${process.env.DIRECT_IMAGE_HOST}/${url}`;
        }
    }

    static getAbsoluteImageUrlForPcode(url: string): string {
        const isDirect = process.env.REACT_APP_TARGET === 'direct';

        if (isDirect) {
            return `${window.location.origin}/images/${url}`;
        } else {
            return `${process.env.DIRECT_IMAGE_HOST}/images/${url}`;
        }
    }

    static getTld(): Tld {
        const hostname = document.location.hostname;
        const resultTldKey = Object.keys(Tld).find(key => {
            const tldValue = Tld[key];

            return endsWith(hostname, tldValue);
        });

        if (resultTldKey === undefined) {
            return Tld.ru;
        }

        return Tld[resultTldKey];
    }

    static getAdvUrl(path: string, tld: Tld): string {
        if ([Tld.kz, Tld.by].includes(tld)) {
            tld = Tld.ru;
        }

        return `//yandex.${tld}/adv/${path}`;
    }

    static getCommonHelpUrl(path: string, tld: Tld): string {
        if ([Tld.kz, Tld.by].includes(tld)) {
            tld = Tld.ru;
        }

        return `//yandex.${tld}/support/${path}`;
    }

    static getCommonTooltipUrl(id: string, tld: Tld): string {
        if ([Tld.kz, Tld.by].includes(tld)) {
            tld = Tld.ru;
        }

        const lang = LangUtils.getCurrentLang();

        return `//direct.yandex.${tld}/tooltip.html?id=${id}&lang=${lang}`;
    }

    static getLegalUrl(path: string, tld: Tld, lang: Lang): string {
        const domainAndLangToLegalTld = {
            [Tld.ru]: {
                [Lang.ru]: Tld.ru,
                [Lang.en]: Tld.ru,
                [Lang.uk]: Tld.ua
            },
            [Tld.by]: {
                [Lang.ru]: Tld.ru,
                [Lang.en]: Tld.ru,
                [Lang.uk]: Tld.ua
            },
            [Tld.kz]: {
                [Lang.ru]: Tld.ru,
                [Lang.en]: Tld.ru,
                [Lang.uk]: Tld.ua
            },
            [Tld.com]: {
                [Lang.ru]: Tld.ru,
                [Lang.en]: Tld.ru,
                [Lang.uk]: Tld.ru
            },
            [Tld.ua]: {
                [Lang.ru]: Tld.ua,
                [Lang.en]: Tld.ru,
                [Lang.uk]: Tld.ua
            },
            [Tld.comTr]: {
                [Lang.en]: Tld.comTr,
                [Lang.tr]: Tld.comTr
            }
        };

        return `//yandex.${
            domainAndLangToLegalTld[tld][lang]
        }/legal/${path}?lang=${lang}`;
    }

    static getGuaranteeLetterDownloadUrl(
        bid: string,
        topicId: string,
        isAgency: boolean
    ): string {
        return UrlHelpers.getDirectLink('downloadGuaranteeLetter', {
            bid: bid,
            topicid: topicId,
            agency: isAgency ? '1' : '0'
        });
    }

    static getWordStatUrl(): string {
        const currentTld = UrlHelpers.getTld();
        let tld = currentTld;

        if (currentTld === Tld.comTr) {
            tld = Tld.com;
        }

        return `//wordstat.yandex.${tld}?direct=1`;
    }

    static getDocCenterUrl(): string {
        return `/doccenter/direct-tooltips/`;
    }

    static isUrl(value: string): boolean {
        return /^\s*(?:http(s)?:\/\/)?(?:(?:[_a-zа-яёЄІЇєіїҐґ0-9][_a-zа-яёЄІЇєіїҐґ0-9-]*)\.)+([a-z0-9а-яёЄІЇєіїҐґ]{2,15}|xn--[a-z0-9-]+)(?:\s*$|\/|:\d+|\?|#)/i.test(
            value
        );
    }

    static getWelcomePageUrl(retpath?: string): string {
        return UrlHelpers.createNewDirectLink(
            `${UrlHelpers.createNewDirectUrlPathByParams('welcome')}/`,
            {
                retpath: retpath ? retpath : null
            }
        );
    }

    static normalizeQuery(
        query: Partial<Record<string, null | undefined | string>>
    ): Query {
        return Object.keys(query).reduce((acc, key) => {
            const value = query[key];

            if (value || value === '') {
                acc[key] = value;
            }
            return acc;
        }, {});
    }

    static getMobileAppUrl(): string {
        return isMacDevice()
            ? 'https://itunes.apple.com/ru/app/id583627331'
            : 'https://play.google.com/store/apps/details?id=ru.yandex.direct';
    }

    static getCommanderPromoUrl(): string {
        return '/commander';
    }

    static getToolsUrl(): string {
        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams(TOOLS_PREFIX),
            {}
        );
    }

    static getAddPerformanceCreativesUrl(login: string): string {
        const url = new URL(document.location.href);

        return UrlHelpers.FormatUrl({
            protocol: 'https',
            host: url.host,
            path: '/smart-designer/add',
            query: {
                client_login: login,
                compact: 1
            }
        });
    }

    static getEditPerformanceCreativeGroupUrl(
        login: string,
        groupId: string
    ): string {
        const url = new URL(document.location.href);

        return UrlHelpers.FormatUrl({
            protocol: 'https',
            host: url.host,
            path: `/smart-designer/${groupId}/edit`,
            query: {
                client_login: login
            }
        });
    }

    static getMetrikaUrl(tld: Tld): string {
        return `//metrika.yandex.${tld}`;
    }

    static getMetrikaGoalsLink(tld: Tld): string {
        return `${UrlHelpers.getMetrikaUrl(tld)}/list`;
    }

    static getAddMetrikaCounterLink(tld: Tld): string {
        return `${UrlHelpers.getMetrikaUrl(tld)}/add`;
    }

    static getMetrikaCounterLink(
        tld: Tld,
        counterId: string,
        campaignExportId?: string
    ): string {
        // нестандартная кодировка, описана тут
        // https://st.yandex-team.ru/METR-21342#1463559252000
        const orderIdParam = campaignExportId
            ? encodeURIComponent(
                  "filter=(ym:s:lastDirectClickOrder=='${campaignExportId}')".replace(
                      /'/g,
                      '%27'
                  )
              )
            : '';

        return `${UrlHelpers.getMetrikaUrl(
            tld
        )}/stat/direct_summary?id=${counterId}&attribution=Last${orderIdParam}`;
    }

    static getAudienceSegmentsLink(tld: Tld): string {
        return `//audience.yandex.${tld}`;
    }

    static getRequestAccessToMetrikaCountersLink(login: string): string {
        return UrlHelpers.getDirectLink('requestAccessToMetrikaCounters', {
            ulogin: login
        });
    }

    static getVCardForPreviewDataUrl = (cid: string, bid: string): string => {
        return `${
            window.location.origin
        }/registered/main.pl?cmd=showContactInfo&cid=${cid}&bid=${bid}`;
    };

    static getDPAUrl(): string {
        return 'https://yandex.com/legal/direct_dpa_ch/';
    }

    static getCanvasIframeUrl(
        canvasDomain: string,
        pageType: string,
        data: {}
    ): string {
        return `${canvasDomain}/${pageType}?shared_data=${encodeURIComponent(
            JSON.stringify(data)
        )}`;
    }

    static getAudioCreativePresetntUrl(
        canvasDomain: string,
        pageType: string,
        data: {},
        canvasType: VideoCanvasPageType
    ): string {
        if (canvasType === VideoCanvasPageType.presets) {
            return `${canvasDomain}/presets/301/creatives/add?shared_data=${encodeURIComponent(
                JSON.stringify(data)
            )}`;
        }

        return `${canvasDomain}/${pageType}?shared_data=${encodeURIComponent(
            JSON.stringify(data)
        )}`;
    }

    static getOutdoorCreativePresentsUrl(
        canvasDomain: string,
        pageType: string,
        data: {},
        canvasType: VideoCanvasPageType
    ): string {
        if (canvasType === VideoCanvasPageType.presets) {
            return `${canvasDomain}/presets/101/creatives/add?shared_data=${encodeURIComponent(
                JSON.stringify(data)
            )}`;
        }

        return this.getCanvasIframeUrl(canvasDomain, pageType, data);
    }

    static getIndoorCreativePresentsUrl(
        canvasDomain: string,
        pageType: string,
        data: {},
        canvasType: VideoCanvasPageType
    ): string {
        if (canvasType === VideoCanvasPageType.presets) {
            return `${canvasDomain}/presets/201/creatives/add?shared_data=${encodeURIComponent(
                JSON.stringify(data)
            )}`;
        }

        return this.getCanvasIframeUrl(canvasDomain, pageType, data);
    }

    static getCanvasCreativePreviewUrl(
        canvasDomain: string,
        creativeId: string
    ): string {
        return `https://${canvasDomain}.yandex.ru/creatives/${creativeId}/preview?isCompactPreview=true`;
    }

    static getCanvasBrandLiftPreviewUrl(
        clientId: string,
        brandSurveyId: string
    ): string {
        return `https://pythia-staging.common.yandex.ru/brandlift/survey/${brandSurveyId}?shared_data=${encodeURIComponent(
            JSON.stringify({ clientId })
        )}&_bl_no_header=1`;
    }

    static getCanvasBrandLiftPreviewStatisticUrl(
        clientId: string,
        brandSurveyId: string
    ): string {
        return `https://pythia-staging.common.yandex.ru/brandlift/statistics/${brandSurveyId}?shared_data=${encodeURIComponent(
            JSON.stringify({ clientId })
        )}&_bl_no_header=1`;
    }

    static getHTML5CreativePreviewUrl(
        canvasDomain: string,
        creativeId: string
    ): string {
        return `https://${canvasDomain}.yandex.ru/html5/${creativeId}/preview?pcodever=${pcodeVer}`;
    }

    static getCanvasDomain(canvasDomain: string): string {
        return `https://${canvasDomain}.yandex.${UrlHelpers.getTld()}`;
    }

    static isUrlValid(url: string): boolean {
        try {
            const urlObj = new URL(url);

            return !!urlObj;
        } catch (err) {
            return false;
        }
    }

    static isDomainValid(url: string): boolean {
        return /^https?:\/\/([а-яёa-z\d]+\.)+(?:[а-яёa-z]+)$/.test(url);
    }

    static isTurboPageUrl(url: string): boolean {
        const turboPageUrlPattern = new RegExp(
            '^https:\\/\\/yandex.ru\\/turbo[^ "]+$'
        );
        return this.isUrlValid(url) && turboPageUrlPattern.test(url);
    }

    static getToken(): string {
        return Cookie.get('_direct_csrf_token') || '';
    }

    static getFromUrlPathAndQuery = (
        url: string
    ): {
        path: string;
        query?: Query;
    } => {
        const startIndexQuery = url.indexOf('?');
        const path =
            startIndexQuery !== -1 ? url.slice(0, startIndexQuery) : url;
        let resultQuery;

        if (startIndexQuery !== -1) {
            const stringQuery = url.slice(startIndexQuery, url.length);
            resultQuery = UrlHelpers.parseQueryString(stringQuery);
        }

        return {
            path: path,
            query: resultQuery
        };
    };

    static parseQueryString(query: string): Record<string, string> {
        if (query === '') {
            return {};
        }

        if (!query || query[0] !== '?') {
            throw new Error('is not valid query');
        }

        const listParams = query.slice(1, query.length).split('&');

        return listParams.reduce((acc, paramString) => {
            const parseParam = paramString.split('=');

            if (parseParam.length === 2) {
                acc[parseParam[0]] = decodeURIComponent(parseParam[1]);
            }

            return acc;
        }, {});
    }

    static getCanvasConstructorUrl(
        canvasDomain: string,
        pageType: CanvasPageType,
        params: string
    ): string {
        return `${canvasDomain}/${pageType}?shared_data=${params}`;
    }

    static getGeoProductCanvasConstructorUrl(
        canvasDomain: string,
        pageType: CanvasPageType,
        params: string
    ): string {
        return `${canvasDomain}/${pageType}/10/creatives/add?shared_data=${params}`;
    }

    static getSpravIframeUrl(baseUrl?: string): string {
        const defaultBase = 'https://yandex.{tld}/sprav';
        const iframeUrlTld = (baseUrl || defaultBase).replace(
            '{tld}',
            UrlHelpers.getTld()
        );

        return `${iframeUrlTld}/iframe/direct/companies`;
    }

    static getSpravIframeUrlWithParams(
        iframeUrl: string,
        clientId?: string
    ): string {
        const searchParams = UrlHelpers.createSearchStringByParams({
            cheif_uid: clientId
        });

        return `${iframeUrl}?${searchParams}`;
    }
}
